package com.elan.edubridge.shared.domain.usecase

import com.elan.edubridge.shared.domain.model.AcademyAttendanceStats
import com.elan.edubridge.shared.domain.model.AttendanceStats
import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.ClassAttendanceStat
import com.elan.edubridge.shared.domain.model.DailyAttendanceStat
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.StudentAttendanceStat
import com.elan.edubridge.shared.domain.model.StudentStatsView
import com.elan.edubridge.shared.domain.repository.EduConnectRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope

/**
 * 기간 출결 통계.
 *
 * 집계는 서버가 아니라 여기서 한다 — 기간 원장을 **한 번** 읽고 그 목록을 날짜별·반별·학생별로
 * 세 번 접는다. 축마다 따로 쿼리하면 같은 문서를 세 번 읽게 된다.
 *
 * 접는 로직이 순수 함수라 저장소 없이도 단위 테스트가 가능하다([foldRoster] 등).
 */
class GetAttendanceStatsUseCase(
    private val repository: EduConnectRepository,
) {
    /**
     * 학원 통계(스태프 전용). [classId]가 null이면 학원 전체다 — ADMIN만 통과한다.
     * 선생님은 배정 반 id를 넘겨야 rules를 통과한다(§4.5). **스태프 화면은 이 함수 대신
     * [forStaff]를 부르는 것이 맞다** — 그쪽이 이 구분을 대신 판단한다.
     *
     * 권한이 없거나 네트워크가 끊기면 저장소가 빈 목록을 주므로 결과는 0건 통계가 된다.
     */
    suspend operator fun invoke(
        academyId: String,
        startDate: String,
        endDate: String,
        classId: String?,
    ): AcademyAttendanceStats =
        fold(academyId, repository.attendanceRecordsRange(academyId, startDate, endDate, classId, null), startDate, endDate)

    /**
     * 스태프 화면 진입점. ADMIN은 전학원 단일 조회, EDITOR는 **배정 반별로 나눠 읽어 합친다.**
     *
     * 나누는 이유: rules `canReadAttendanceOf`(§4.5)는 EDITOR에게 배정 반만 연다. classId 없는
     * 전학원 쿼리는 배정 안 된 반을 포함하므로 Firestore가 쿼리를 **통째로** 거부하고, 실패=빈
     * 목록 계약이 그것을 "기록 없음"으로 위장한다 — 학원장은 통계가 보이는데 선생님만 오류
     * 없이 빈 화면이던 원인이다. 반별 쿼리는 각각 rules를 통과한다.
     *
     * 반별 조회는 병렬이다. 반이 서로소라(한 원장 행은 정확히 한 반에 속한다) 병합에 중복이
     * 없고, 한 반이 실패해도 그 반만 빠진다 — 전체가 비는 것보다 낫다.
     */
    suspend fun forStaff(
        session: SessionState,
        academyId: String,
        startDate: String,
        endDate: String,
    ): AcademyAttendanceStats {
        val rows = if (session.activePermission == Permission.ADMIN) {
            repository.attendanceRecordsRange(academyId, startDate, endDate, null, null)
        } else {
            coroutineScope {
                session.activeAssignedClassIds.map { classId ->
                    async { repository.attendanceRecordsRange(academyId, startDate, endDate, classId, null) }
                }.awaitAll().flatten()
            }
        }
        return fold(academyId, rows, startDate, endDate)
    }

    private fun fold(
        academyId: String,
        rows: List<DailyRosterEntry>,
        startDate: String,
        endDate: String,
    ): AcademyAttendanceStats = AcademyAttendanceStats(
        academyId = academyId,
        overall = foldRoster(rows, startDate, endDate),
        byDate = byDate(rows),
        byClass = byClass(rows, startDate, endDate),
        byStudent = byStudent(rows, startDate, endDate),
    )

    /**
     * 한 학생의 통계를 **스태프 시점**으로 본다 — 기간 원장 기반이라 분모가 있어 출석률이 유효하다.
     */
    suspend fun forStudent(
        academyId: String,
        startDate: String,
        endDate: String,
        studentId: String,
    ): StudentStatsView {
        val rows = repository.attendanceRecordsRange(academyId, startDate, endDate, null, studentId)
        return StudentStatsView(
            studentId = studentId,
            // 이름은 기록에 복사된 스냅샷이다. 개명한 학생은 옛 기록에 옛 이름으로 남으므로 가장 최근 기록의 이름을 쓴다.
            studentName = rows.maxByOrNull { it.serviceDate }?.studentName ?: "",
            stats = foldRoster(rows, startDate, endDate),
            byDate = byDate(rows),
            fromRoster = true,
        )
    }

    companion object {
        /**
         * 기간 원장을 접는다. 한 행이 곧 (학생 × 수업일) 1건이라 그대로 세면 된다.
         */
        fun foldRoster(
            rows: List<DailyRosterEntry>,
            startDate: String,
            endDate: String,
        ): AttendanceStats =
            count(rows.map { it.status }, rows.count { it.lateArrival }, startDate, endDate)

        /**
         * 학생·보호자가 자기 미러([AttendanceTimelineEntry])로 보는 통계.
         *
         * 미러에는 **등원 이벤트만** 있다 — 오지 않은 날은 이벤트가 없어 데이터에 존재하지 않는다.
         * 그래서 [StudentStatsView.fromRoster]가 false이고 화면은 출석률을 띄우면 안 된다.
         *
         * 하루에 등원·하원 두 건이 오므로 (날짜, 반)마다 **가장 최근 이벤트 하나로 접는다** —
         * 그래야 기간 원장과 같은 단위가 되어 횟수가 두 배로 세어지지 않는다.
         *
         * [AttendanceTimelineEntry]에는 [DailyRosterEntry.lateArrival]에 해당하는 필드가 없다.
         * 대신 접기 **전** 슬롯에 `LATE` 이벤트가 하나라도 있었는지를 본다 — 최신 하나만 보면
         * 지각 등원 후 하원한 날이 `LEFT`로 접혀 지각이 사라진다.
         */
        fun fromTimeline(
            entries: List<AttendanceTimelineEntry>,
            studentId: String,
            studentName: String,
            startDate: String,
            endDate: String,
        ): StudentStatsView {
            val inRange = entries.filter { it.serviceDate in startDate..endDate }
            val slots: List<Pair<AttendanceTimelineEntry, Boolean>> = inRange
                .groupBy { it.serviceDate to it.classId }
                .values
                .mapNotNull { slot ->
                    val latest = slot.maxByOrNull { it.occurredAt } ?: return@mapNotNull null
                    latest to slot.any { it.status == AttendanceStatus.LATE }
                }
            return StudentStatsView(
                studentId = studentId,
                studentName = studentName,
                stats = count(
                    slots.map { it.first.status },
                    slots.count { it.second },
                    startDate,
                    endDate,
                ),
                byDate = slots
                    .groupBy { it.first.serviceDate }
                    .map { (date, daySlots) ->
                        DailyAttendanceStat(
                            date,
                            count(daySlots.map { it.first.status }, daySlots.count { it.second }, date, date),
                        )
                    }
                    .sortedBy { it.serviceDate },
                fromRoster = false,
            )
        }

        /** [lateArrivals]는 [statuses]와 직교하는 축이라 따로 받는다([AttendanceStats.lateArrivalCount]). */
        private fun count(
            statuses: List<AttendanceStatus>,
            lateArrivals: Int,
            startDate: String,
            endDate: String,
        ): AttendanceStats = AttendanceStats(
            startDate = startDate,
            endDate = endDate,
            presentCount = statuses.count { it == AttendanceStatus.PRESENT },
            lateCount = statuses.count { it == AttendanceStatus.LATE },
            absentCount = statuses.count { it == AttendanceStatus.ABSENT },
            leftCount = statuses.count { it == AttendanceStatus.LEFT },
            notArrivedCount = statuses.count { it == AttendanceStatus.NOT_ARRIVED },
            lateArrivalCount = lateArrivals,
        )

        /**
         * 추이 그래프용. 날짜 오름차순이라 화면이 다시 정렬하지 않아도 된다.
         *
         * 각 칸의 구간은 그 날짜 하루다 — 전체 구간이 아니라 `date..date`로 접는다.
         */
        fun byDate(rows: List<DailyRosterEntry>): List<DailyAttendanceStat> = rows
            .groupBy { it.serviceDate }
            .map { (date, dayRows) -> DailyAttendanceStat(date, foldRoster(dayRows, date, date)) }
            .sortedBy { it.serviceDate }

        /** 반 비교용. 이름 오름차순. */
        fun byClass(
            rows: List<DailyRosterEntry>,
            startDate: String,
            endDate: String,
        ): List<ClassAttendanceStat> = rows
            .groupBy { it.classId }
            .map { (classId, classRows) ->
                ClassAttendanceStat(
                    classId = classId,
                    // 이름은 서버가 기록마다 복사해 두는데, 스케줄러가 만든 '미등원' 뼈대 기록에는
                    // 한동안 빠져 있었다(roster.ts, 2026-09-09 수정). 그 기록이 그룹의 첫 줄이면
                    // 반 이름이 빈칸으로 그려지므로, 그룹 안에서 이름이 있는 줄을 찾아 쓴다.
                    className = classRows.sortedByDescending { it.serviceDate }
                        .firstNotNullOfOrNull { it.className.takeIf(String::isNotBlank) }.orEmpty(),
                    stats = foldRoster(classRows, startDate, endDate),
                )
            }
            .sortedBy { it.className }

        /** 학생 표용. 이름 오름차순. */
        fun byStudent(
            rows: List<DailyRosterEntry>,
            startDate: String,
            endDate: String,
        ): List<StudentAttendanceStat> = rows
            .groupBy { it.studentId }
            .map { (studentId, studentRows) ->
                StudentAttendanceStat(
                    studentId = studentId,
                    // 이름은 기록마다 복사된 스냅샷 — 개명한 학생은 옛 기록에 옛 이름으로 남는다. 최신 기록의 이름을 쓴다.
                    studentName = studentRows.maxByOrNull { it.serviceDate }?.studentName ?: studentRows.first().studentName,
                    stats = foldRoster(studentRows, startDate, endDate),
                )
            }
            .sortedBy { it.studentName }
    }
}
